/**
 * Retirement Village (Independent Living) – calculations only.
 * Does NOT call or alter calculateAgedCareCosts, computeMeansTestedFees, getMtaBreakdown.
 * May call calculateFinancials with prepared factFind for pension/cashflow.
 *
 * Ported verbatim from frontend/src/calculations/RetirementVillageCalculations.js.
 */

import { villageInterestHeld, withVillagePayoutBanked } from './support/villageExit.ts';
import { calculateFinancials, calculateAge } from '../../../server/src/rest/calculation/assessment/financialCalculations.ts';
import { applyDownsizerContributionsToAssets } from './downsizerContributionUtils.ts';
import { getAustralianFYEndingYearContainingDate } from './giftingDeprivation.ts';
import { newMoneyProfileOf, personalSplitFor, ASSET_CLASS_ASSUMPTIONS } from './investmentProfiles.ts';
import { markAbpFundedAcquisitionAfterLiquidation } from './abpProjectionUtils.ts';
import { buildAnnuityImpact, applyAnnuityToFactFind, annuityAssetFromRecord } from './annuityStrategies.ts';
import { computeDisposalCgt } from './disposalCgt.ts';
import { resolveDisposalTaxContext } from './disposalTaxContext.ts';
import { markAbpFundedAcquisitionAfterLiquidation as markAbpAfterLiquidation } from './abpProjectionUtils.ts';
import { getInvestmentPropertyTaxSummary } from './entityDisposalTax.ts';
import { entityOwnershipValue, getEntityIdFromOwnership, isEntityOwnership } from './constants/clientStructureTypes.ts';
import { debitCgtFromOwnershipPool, resolveRadFundingEntry } from './support/entityFundingUtils.ts';
import { creditLiquidProceeds, removeSoldInvestmentProperty, routesIpProceedsToEntityPool } from './support/entityInvestmentPropertySale.ts';
import type { FactFind, FactFindAsset } from './factFindTypes.ts';

// Extra allowable amount (entry fee above this = homeowner for Age Pension) — the one published
// figure, not a local copy that can drift from it (it did: 258,000 here against 267,000 from 1 July 2026).
import { EXTRA_ALLOWABLE_AMOUNT } from './constants/ratesAndThresholds.ts';

/**
 * Compute DMF (departure fee) and exit entitlement for retirement village.
 */
export function computeDmf(entryFee: number, yearsResidency: number, dmfPctPerYear: number, dmfCapYears = 12, dmfBasis: 'entry' | 'resale' = 'entry', resaleValue = 0) {
    const effectiveYears = Math.min(Math.max(0, yearsResidency), dmfCapYears || 99);
    const pctApplied = (dmfPctPerYear / 100) * effectiveYears;
    const basisValue = dmfBasis === 'resale' && resaleValue > 0 ? resaleValue : entryFee;
    const dmfAmount = basisValue * Math.min(pctApplied, 1);
    return { dmfAmount, dmfPctApplied: pctApplied * 100 };
}

/**
 * Exit entitlement to resident: resale (or entry + growth) minus DMF, minus operator's share of capital gain, plus resident's share.
 */
export function computeExitEntitlement(
    entryFee: number,
    yearsResidency: number,
    growthRatePct: number,
    capitalGainShareToResident: number,
    dmfParams: any,
    reinstatementCost = 0,
) {
    const growthRate = (growthRatePct || 0) / 100;
    const resaleValue = entryFee * Math.pow(1 + growthRate, yearsResidency);
    const capitalGain = Math.max(0, resaleValue - entryFee);
    const residentShareOfGain = capitalGain * (capitalGainShareToResident ?? 0.5);
    const operatorShareOfGain = capitalGain - residentShareOfGain;
    const { dmfAmount } = computeDmf(entryFee, yearsResidency, dmfParams.dmfPctPerYear, dmfParams.dmfCapYears, dmfParams.dmfBasis || 'entry', resaleValue);
    const reinstatement = reinstatementCost || 0;
    const netToResident = entryFee + residentShareOfGain - dmfAmount - reinstatement;
    return { resaleValue, dmfAmount, capitalGain, residentShareOfGain, operatorShareOfGain, netToResident };
}

/**
 * Summarise village costs for display (entry, ongoing annual, exit estimate).
 */
export function getVillageCostSummary(villageInputs: any) {
    const entryFee = villageInputs.entryFee || 0;
    const ongoing = villageInputs.ongoingWeeklyOrMonthly || 0;
    const freq = villageInputs.ongoingFrequency || 'monthly';
    const annualOngoing = freq === 'weekly' ? ongoing * 52 : ongoing * 12;
    const years = Math.max(0, villageInputs.yearsResidency || 0);
    const dmfParams = {
        dmfPctPerYear: villageInputs.dmfPctPerYear || 0,
        dmfCapYears: villageInputs.dmfCapYears || 0,
        dmfBasis: villageInputs.dmfBasis || 'entry',
    };
    const exit = computeExitEntitlement(
        entryFee,
        years,
        villageInputs.growthRatePct || 0,
        villageInputs.capitalGainShareToResident ?? 0.5,
        dmfParams,
        villageInputs.reinstatementCap || 0,
    );
    return {
        entryFee,
        annualOngoing,
        yearsResidency: years,
        exitResaleValue: exit.resaleValue,
        exitDmf: exit.dmfAmount,
        exitNetToResident: exit.netToResident,
        ...exit,
    };
}

/**
 * Build simulated factFind for pension/cashflow when resident is in village (for use with calculateFinancials).
 * Caller should pass base factFind and strategy: e.g. home sold (no home, proceeds used for entry + downsizer + residual), or home rented.
 * This helper only adjusts home/value and optional entry-fee-as-principal-home placeholder; it does NOT implement full sell/rent/downsizer logic.
 * Full strategy-to-factFind mapping should live in the page/analyser.
 */
export function buildSimulatedFactFindForVillage(baseFactFind: FactFind, options: any = {}) {
    const data = structuredClone(baseFactFind);
    if (options.noPrincipalHome) {
        if (data.home) data.home.value = Number(options.homeValueForPension ?? 0);
    }
    return data;
}

// Downsizer contribution cap per person (ATO; update from Schedule). Same as used elsewhere in app.
const DOWNSIZER_CONTRIBUTION_CAP = 300000;

/**
 * Net proceeds toward RV entry fee for an IP allocation (entity-aware tax when structure-owned).
 */
export function computeRvIpNetForAllocation(factFind: FactFind, propertyAsset: FactFindAsset | null | undefined, grossAmount: number) {
    const value = propertyAsset?.value || 0;
    const saleAmount = Math.min(Math.max(0, grossAmount || 0), value);
    if (saleAmount <= 0) return { netProceeds: 0, cgt: 0, sellingCosts: 0 };

    if (propertyAsset && isEntityOwnership(propertyAsset.ownership)) {
        const details = {
            selectedPropertyId: propertyAsset.id,
            salePrice: saleAmount,
            sellingCosts: saleAmount * 0.025,
            costBase: propertyAsset.costBase ?? '',
        };
        const summary = getInvestmentPropertyTaxSummary(factFind, details);
        const netProceeds = summary?.netProceeds ?? 0;
        const cgt = summary?.effectiveCGT ?? summary?.estimatedCGT ?? 0;
        return { netProceeds, cgt, sellingCosts: saleAmount * 0.025 };
    }

    return computeInvestmentPropertyNetForAmount(factFind, propertyAsset, saleAmount);
}

/** Total net $ toward entry fee (Option A on shares; entity-aware IP net). */
export function computeRvEntryFeeFundingTotals(factFind: FactFind, entryFeeFunding: Record<string, any> = {}, cgtOverrides: any = {}) {
    const liquidFunding: Record<string, any> = {};
    let ipNetTotal = 0;
    let ipCgtTotal = 0;

    Object.entries(entryFeeFunding || {}).forEach(([assetKey, amount]) => {
        const gross = amount || 0;
        if (gross <= 0) return;

        if (assetKey.startsWith('investmentProperty-')) {
            const idRaw = assetKey.replace(/^investmentProperty-/, '');
            const asset = factFind?.assets?.investmentProperty?.find((a) => String(a.id) === String(idRaw));
            if (!asset) return;
            const { netProceeds, cgt } = computeRvIpNetForAllocation(factFind, asset, gross);
            ipNetTotal += netProceeds;
            ipCgtTotal += cgt;
        } else {
            liquidFunding[assetKey] = gross;
        }
    });

    const { netTotal: liquidNet, shareCgtTotal } = resolveRvLiquidFundingNet(factFind, liquidFunding, cgtOverrides);

    return {
        netTotal: liquidNet + ipNetTotal,
        shareCgtTotal,
        ipCgtTotal,
        cgtTotal: shareCgtTotal + ipCgtTotal,
    };
}

function resolveRvLiquidFundingNet(factFind: FactFind, liquidFunding: Record<string, any>, cgtOverrides: any) {
    let grossTotal = 0;
    let netTotal = 0;
    let shareCgtTotal = 0;

    Object.entries(liquidFunding || {}).forEach(([assetKey, amount]) => {
        const gross = Math.max(0, amount || 0);
        if (gross <= 0) return;
        grossTotal += gross;
        const entry = resolveRadFundingEntry(factFind, assetKey, gross, cgtOverrides);
        netTotal += entry.net;
        shareCgtTotal += entry.cgt;
    });

    return { grossTotal, netTotal, shareCgtTotal };
}

/**
 * Draws `remaining` of the entry contribution from the liquid assets in the default order — bank,
 * term deposits, account-based pensions, shares — and returns what it managed to take. Mutates
 * `data.assets`. Stops when the money runs out: a village entry cannot be part-paid, and the
 * caller reports the shortfall rather than pretending (`entryFeePaid`).
 */
function drawEntryFeeFromLiquidInOrder(data: any, remaining: number): number {
    const assets = data.assets || {};
    let left = Math.max(0, remaining);
    let deducted = 0;
    if (left <= 0) return 0;
    (['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares'] as const).forEach((type) => {
        if (left <= 0 || !assets[type]) return;
        (assets[type] || []).forEach((asset: any) => {
            if (left <= 0) return;
            const val = asset.value || 0;
            const deduct = Math.min(val, left);
            if (deduct > 0) {
                asset.value = Math.max(0, val - deduct);
                left -= deduct;
                deducted += deduct;
                if (type === 'accountBasedPensions') {
                    markAbpFundedAcquisitionAfterLiquidation(asset, data, 1);
                }
            }
        });
    });
    return deducted;
}

/**
 * THE REST COMES FROM SAVINGS (owner-caught, 24 Sep 2026). A property route paid what its sale
 * netted and stopped: an $800,000 entry contribution on $651,966 of net proceeds booked the full
 * $800,000 as paid — the exit entitlement struck on money that never went in — while the screen said
 * "paid in full from selling the investment property". A village contribution has no pay-part-of-it
 * option, so whatever the sale does not cover comes from savings: the adviser's own allocation first
 * (`entryFeeFunding`, capped at the remainder), then the default draw order, and whatever is still
 * short is genuinely unpaid and is reported as such through `entryFeePaid`.
 */
function topUpEntryFeeFromLiquid(data: any, baseFactFind: FactFind, rvStrategy: any, remaining: number): { deducted: number; entryFeeCgtFromIp: number } {
    if (!(remaining > 0)) return { deducted: 0, entryFeeCgtFromIp: 0 };
    const { totalTowardEntry, entryFeeCgtFromIp } = applyRvLiquidEntryFeeFunding(
        data, baseFactFind, rvStrategy?.entryFeeFunding || {}, rvStrategy?.entryFeeCgtOverrides || {}, remaining,
    );
    const deducted = totalTowardEntry + drawEntryFeeFromLiquidInOrder(data, remaining - totalTowardEntry);
    return { deducted, entryFeeCgtFromIp };
}

/**
 * Apply liquid entry-fee funding map to cloned factFind (entity pool debits + IP residual routing).
 */
export function applyRvLiquidEntryFeeFunding(data: any, baselineFactFind: FactFind, entryFeeFunding: Record<string, any>, cgtOverrides: any = {}, entryFeeCap: number = Infinity) {
    let totalTowardEntry = 0;
    let entryFeeCgtFromIp = 0;
    let entryRemaining = entryFeeCap != null && Number.isFinite(entryFeeCap) ? Math.max(0, entryFeeCap || 0) : Infinity;

    Object.entries(entryFeeFunding || {}).forEach(([assetId, amount]) => {
        const gross = amount || 0;
        if (gross <= 0) return;

        const parts = assetId.split('-');
        const type = parts[0]!;
        const idRaw = parts.length > 1 ? (Number.isNaN(Number(parts[1])) ? parts.slice(1).join('-') : parts[1]) : null;
        if (!data.assets?.[type] || idRaw == null) return;

        const idx = data.assets[type].findIndex((a: any) => String(a.id) === String(idRaw));
        if (idx === -1) return;

        const asset = data.assets[type][idx];
        const val = asset.value || 0;
        const deduct = Math.min(gross, val);
        if (deduct <= 0) return;

        if (type === 'investmentProperty') {
            const { netProceeds, cgt } = computeRvIpNetForAllocation(baselineFactFind, asset, deduct);
            const isFullSale = deduct >= val;

            if (isFullSale) {
                removeSoldInvestmentProperty(data.assets, asset.id);
            } else {
                asset.value = Math.max(0, val - deduct);
            }

            const towardEntry = Math.min(netProceeds, entryRemaining);
            totalTowardEntry += towardEntry;
            entryRemaining -= towardEntry;
            entryFeeCgtFromIp += cgt;

            if (isFullSale && routesIpProceedsToEntityPool(baselineFactFind, asset)) {
                const entityId = getEntityIdFromOwnership(asset.ownership);
                const structure = (baselineFactFind.structures || []).find((s) => s.id === entityId);
                const name = structure?.name?.trim() || 'Trust or company';
                const residual = Math.max(0, netProceeds - towardEntry);
                if (residual > 0) {
                    creditLiquidProceeds(data.assets, residual, {
                        ownership: entityOwnershipValue(entityId!),
                        id: `rv-entry-ip-residual-${entityId}`,
                        description: `Residual from investment property sale — ${name} (after village entry)`,
                        mergeEntityId: entityId!,
                    });
                }
            } else if (isFullSale) {
                asset.rent = 0;
                if (asset.expenses != null) asset.expenses = 0;
            }
        } else if (type === 'shares') {
            const { gross: shareGross, net, cgt } = resolveRadFundingEntry(baselineFactFind, assetId, deduct, cgtOverrides);
            const towardEntry = Math.min(net, entryRemaining);
            asset.value = Math.max(0, val - shareGross);
            totalTowardEntry += towardEntry;
            entryRemaining -= towardEntry;
            debitCgtFromOwnershipPool(data.assets, cgt, asset.ownership);
        } else {
            const towardEntry = Math.min(deduct, entryRemaining);
            asset.value = Math.max(0, val - deduct);
            totalTowardEntry += towardEntry;
            entryRemaining -= towardEntry;
            if (type === 'accountBasedPensions' && deduct > 0) {
                markAbpFundedAcquisitionAfterLiquidation(asset, data, 1);
            }
        }
    });

    return { totalTowardEntry, entryFeeCgtFromIp };
}

/**
 * Net proceeds and CGT for selling `amountToSell` worth of an investment property.
 * Delegates to the single CGT engine (see shared/src/calculation/disposalCgt.ts) — this used to hold
 * its own copy of the math, which was the only one that split a jointly-owned gain correctly.
 */
export function computeInvestmentPropertyNetForAmount(
    factFind: FactFind,
    propertyAsset: FactFindAsset | null | undefined,
    amountToSell: number,
    options: { disposalDate?: Date | string; cpiRate?: number; financials?: any } = {},
) {
    const value = propertyAsset?.value || 0;
    if (value <= 0 || amountToSell <= 0) return { netProceeds: 0, cgt: 0, sellingCosts: 0 };
    const amount = Math.min(amountToSell, value);
    const sellingCosts = amount * 0.025;

    const result = computeDisposalCgt({
        taxContext: resolveDisposalTaxContext(factFind, options.financials),
        asset: propertyAsset,
        salePrice: amount,
        sellingCosts,
        originalAssetValue: value,
        disposalDate: options.disposalDate,
        cpiRate: options.cpiRate,
        structures: factFind?.structures,
    });

    return { netProceeds: Math.max(0, result.netProceeds), cgt: result.estimatedCGT, sellingCosts };
}

function deductFromAssetList(data: any, sourceIdentifier: string, amount: number) {
    if (!amount || amount <= 0) return;
    let sourceType: string;
    let targetAssetId: string | null;
    if (sourceIdentifier.includes('-')) {
        const parts = sourceIdentifier.split('-');
        sourceType = parts[0]!;
        targetAssetId = parts.slice(1).join('-');
    } else {
        sourceType = sourceIdentifier;
        targetAssetId = null;
    }
    if (sourceType === 'bank') sourceType = 'bankAccounts';
    if (!data.assets?.[sourceType]?.length) return;
    let remaining = amount;
    const list = data.assets[sourceType];

    const applyDeduction = (asset: any, index: number) => {
        if (remaining <= 0) return;
        const currentValue = asset.value || asset.balance || 0;
        const deduction = Math.min(remaining, currentValue);
        if (deduction <= 0) return;
        if (asset.balance !== undefined) {
            asset.balance = Math.max(0, currentValue - deduction);
        } else {
            asset.value = Math.max(0, currentValue - deduction);
        }
        remaining -= deduction;
        if (sourceType === 'accountBasedPensions') {
            markAbpAfterLiquidation(asset, data, 1);
        }
        const newValue = asset.value || asset.balance || 0;
        if (newValue <= 0) list.splice(index, 1);
    };

    if (targetAssetId) {
        const idx = list.findIndex((a: any) => String(a.id) === String(targetAssetId));
        if (idx >= 0) applyDeduction(list[idx], idx);
    } else {
        for (let i = 0; i < list.length && remaining > 0; i++) {
            applyDeduction(list[i], i);
        }
    }
}

/**
 * Record a gift as DEPRIVATION, not merely as money that left the balance sheet.
 *
 * THE BUG THIS FIXES (found 2026-08-27). Both village gifting paths — the "Other Strategies" gift
 * from liquid assets, and the gift allocated out of home-sale proceeds — deducted the money from the
 * client's assets and stopped there. Nothing was ever written to `giftsHistory`, so the five-year
 * deprivation rules never fired: the module reported a client gifting $200,000 as immediately
 * $200,000 poorer for means-test purposes, and showed them an Age Pension rise Centrelink would not
 * pay for five years.
 *
 * It flatters the strategy, which is the dangerous direction — a plan built on it would have the
 * family give away money on the strength of a benefit that does not arrive.
 *
 * The aged care, Support at Home and downsize paths have always recorded the gift. This is the same
 * rule, in the same shape: the financial year the gift is MADE in, because that is when the clock
 * starts. `assessableMeans` then applies the free-area and five-year rules to it exactly as it does
 * for a gift made anywhere else — nothing about the assessment itself needed changing.
 */
function recordRvGiftAsDeprivation(data: any, amount: number, asOfDate?: string | Date): void {
    const giftAmount = Math.max(0, Number(amount) || 0);
    if (giftAmount <= 0) return;
    const when = asOfDate ? new Date(asOfDate) : new Date();
    const fyEnd = getAustralianFYEndingYearContainingDate(Number.isNaN(when.getTime()) ? new Date() : when);
    if (!data.giftsHistory) data.giftsHistory = { hasGifted: false };
    const key = `year${fyEnd}`;
    data.giftsHistory[key] = (Number(data.giftsHistory[key]) || 0) + giftAmount;
    data.giftsHistory.hasGifted = true;
}

/**
 * Apply gifting, funeral bonds, and annuity from liquid assets (Aged Care Other Strategies parity).
 */
export function applyOtherStrategiesToRvFactFind(data: any, otherStrategies: any, annuityRecord: any, baseFactFind: FactFind) {
    const result = structuredClone(data);
    if (!otherStrategies) return result;

    if (otherStrategies.annuity && annuityRecord?.purchaseAmount > 0) {
        try {
            const mapped = { ...annuityRecord };
            if (!mapped.age && baseFactFind?.client?.dob) {
                mapped.age = calculateAge(baseFactFind.client.dob);
            }
            if ((mapped.owner === 'joint' || mapped.reversionary) && !mapped.partnerAge && baseFactFind?.partner?.dob) {
                mapped.partnerAge = calculateAge(baseFactFind.partner.dob);
            }
            if (mapped.fundingSource?.id?.includes('-')) {
                const parts = mapped.fundingSource.id.split('-');
                const assetType = parts[0];
                if (assetType === 'bankAccounts') mapped.fundingSource = { ...mapped.fundingSource, type: 'bank' };
                else if (assetType === 'accountBasedPensions') mapped.fundingSource = { ...mapped.fundingSource, type: 'abp' };
                else mapped.fundingSource = { ...mapped.fundingSource, type: assetType };
            }
            const built = buildAnnuityImpact(result, mapped);
            if (built && !built.warnings) {
                if (built.fundingSource?.id?.includes('-')) {
                    const parts = built.fundingSource.id.split('-');
                    const assetType = parts[0];
                    built.fundingSourceId = parts.slice(1).join('-');
                    if (assetType === 'bankAccounts') built.fundingSourceType = 'bank';
                    else if (assetType === 'accountBasedPensions') built.fundingSourceType = 'abp';
                    else built.fundingSourceType = assetType;
                }
                applyAnnuityToFactFind(result, built);
            }
        } catch (e) {
            console.error('RV other strategies: annuity apply failed', e);
        }
    }

    if (otherStrategies.gifting && otherStrategies.giftingAmount > 0) {
        deductFromAssetList(result, otherStrategies.giftingSource || 'bankAccounts', otherStrategies.giftingAmount || 0);
        // The money leaving the balance sheet is only half of it — see recordRvGiftAsDeprivation.
        recordRvGiftAsDeprivation(result, otherStrategies.giftingAmount || 0, otherStrategies.giftDate);
    }

    if (otherStrategies.funeralBonds && otherStrategies.funeralBondAmount > 0) {
        const funeralBondAmount = otherStrategies.funeralBondAmount || 0;
        deductFromAssetList(result, otherStrategies.funeralBondSource || 'bankAccounts', funeralBondAmount);
        if (!result.assets.funeralBonds) result.assets.funeralBonds = [];
        result.assets.funeralBonds.push({
            id: 'rv-other-strategy-funeral-bond',
            type: 'funeralBonds',
            description: 'Funeral bond (other strategies)',
            value: funeralBondAmount,
            ownership: 'client',
        });
    }

    return result;
}

function finalizeRvStrategyFactFind(result: any, baseFactFind: FactFind, rvStrategy: any) {
    const os = rvStrategy?.otherStrategies;
    if (!os || (!os.gifting && !os.funeralBonds && !os.annuity)) return result;
    result.factFind = applyOtherStrategiesToRvFactFind(result.factFind, os, os.annuity ? rvStrategy.annuityRecord : null, baseFactFind);
    return result;
}

/**
 * Public entry: build the post-move factFind, then mark the mover(s) as retirement-village residents and record
 * the entry contribution, so the shared engine applies the EC-vs-EAA homeowner test + Rent Assistance.
 * - Common case EC > EAA: identical to the prior behaviour (entry fee already deducted; homeowner thresholds).
 * - EC <= EAA: corrected to non-homeowner (higher limits), the entry contribution counted as an assessable
 *   asset (not deemed), and Rent Assistance available.
 */
/**
 * The means-test strategy options a village move puts in force, flat in the shape
 * `calculateFinancials` reads: `rentHome`/`rentAmount`/`rentExpenses`/`partnerRent` when the former
 * home is being let, and `formerPrincipalHomeAssessable` when a kept home loses its exemption to the
 * village entry. ONE function on purpose — the server's projection run and the page's plan-in-place
 * assessment (dated entries) must assess the same move under the same options, and two hand-written
 * copies of this mapping are exactly how the rent once reached the projections but not the panel.
 *
 * `movedFactFind` is the POST-move fact-find (the home value read is the kept home's).
 */
export function buildVillageMeansAssessmentOptions(rvStrategy: any, villageInputs: any, movedFactFind: FactFind): Record<string, unknown> {
    const options: Record<string, unknown> = {};
    const fundFrom = rvStrategy?.fundFrom || 'liquid';
    const rentDetails = rvStrategy?.rentDetails as any;
    if ((fundFrom === 'rent_home' || rvStrategy?.rentHome) && rentDetails) {
        options.rentHome = true;
        options.rentAmount = rentDetails.rentAmount || 0;
        options.rentExpenses = rentDetails.rentExpenses || 0;
        if (rentDetails.partnerRent) options.partnerRent = rentDetails.partnerRent;
    }
    const entryFee = villageInputs?.entryFee || 0;
    if (entryFee > 0 && (fundFrom === 'liquid' || fundFrom === 'rent_home' || rvStrategy?.rentHome) && (parseFloat(String((movedFactFind as any)?.home?.value)) || 0) > 0) {
        options.formerPrincipalHomeAssessable = true;
    }
    return options;
}

export function buildSimulatedFactFindForVillageStrategy(factFind: FactFind, rvStrategy: any, villageInputs: any) {
    const result = buildSimulatedFactFindForVillageStrategyInner(factFind, rvStrategy, villageInputs);
    const entryFee = villageInputs?.entryFee || 0;
    if (entryFee > 0 && result?.factFind) {
        if (!result.factFind.client) result.factFind.client = {};
        result.factFind.client.currentResidency = 'retirement_village';
        if (result.factFind.adviceFor === 'couple') {
            if (!result.factFind.partner) result.factFind.partner = {};
            result.factFind.partner.currentResidency = 'retirement_village';
        }
        // The NEW unit's record, and only that: a client moving from another village (8 Oct 2026)
        // must not carry the old unit's fees or exit terms onto the new one — the old unit is paid
        // out by the projection (villageExitPlugin) from the ORIGINAL record.
        result.factFind.retirementVillage = { entryContribution: entryFee };
        // Rent Assistance: a non-homeowner village resident (EC ≤ EAA) gets RA on their ongoing (recurrent)
        // service charges — NOT the entry contribution (DSS Guide 4.6.4.30). Supply the weekly ongoing fee on
        // factFind.rent so the shared CRA logic can pay it. Homeowner residents (EC > EAA) get no RA.
        if (entryFee <= EXTRA_ALLOWABLE_AMOUNT) {
            const weeklyOngoing = (getVillageCostSummary(villageInputs).annualOngoing || 0) / 52;
            if (weeklyOngoing > 0) {
                result.factFind.rent = {
                    ...(result.factFind.rent || {}),
                    rentAmount: weeklyOngoing,
                    landlordType: 'retirement_village',
                    sharedAccommodation: result.factFind.rent?.sharedAccommodation ?? false,
                };
            }
        }
    }
    return result;
}

function buildSimulatedFactFindForVillageStrategyInner(factFind: FactFind, rvStrategy: any, villageInputs: any): any {
    const fundFrom = rvStrategy?.fundFrom || 'liquid';
    const entryFee = villageInputs?.entryFee || 0;
    // Moving from another village with the payout taken as paid at the move (8 Oct 2026): the
    // old unit's money is in the bank before anything is drawn for the new contribution.
    if (villageInputs?.oldUnitPaysAtMove && villageInterestHeld(factFind)) factFind = withVillagePayoutBanked(factFind);

    if (fundFrom === 'rent_home') {
        return finalizeRvStrategyFactFind(
            { factFind: structuredClone(factFind), entryAlreadyPaidFromSale: false, entryFeeCgtFromIp: 0 },
            factFind,
            rvStrategy,
        );
    }

    if (fundFrom === 'investment_property' && rvStrategy?.investmentPropertyDetails && (factFind?.assets?.investmentProperty?.length ?? 0) > 0) {
        const ipDetails = rvStrategy.investmentPropertyDetails;
        const data = structuredClone(factFind);
        if (!data.assets) data.assets = {};
        // Guaranteed non-empty by the investmentProperty.length check above (factFind.assets and
        // data.assets have the same shape — data is a structuredClone of factFind).
        const investmentProperty = data.assets.investmentProperty!;
        const selectedId = ipDetails.selectedPropertyId ?? investmentProperty[0]?.id;
        const ipAsset = investmentProperty.find((p: any) => String(p.id) === String(selectedId)) || investmentProperty[0];
        if (!ipAsset) {
            return finalizeRvStrategyFactFind({ factFind: data, entryAlreadyPaidFromSale: false, entryFeeCgtFromIp: 0 }, factFind, rvStrategy);
        }
        const fullValue = ipAsset.value || 0;
        const { netProceeds, cgt } = computeInvestmentPropertyNetForAmount(factFind, ipAsset, fullValue);
        const entryFeeFromIp = Math.min(entryFee, Math.max(0, ipDetails.entryFeeAllocation || 0), netProceeds);
        const downsizer = Math.min(DOWNSIZER_CONTRIBUTION_CAP, Math.max(0, ipDetails.downsizerContribution || 0));
        const partnerDownsizer = Math.min(DOWNSIZER_CONTRIBUTION_CAP, Math.max(0, ipDetails.partnerDownsizerContribution || 0));
        const giftingAmount = Math.max(0, ipDetails.giftingAmount || 0);
        const funeralBondPurchase = Math.max(0, ipDetails.funeralBondPurchase || 0);
        const totalAllocated = entryFeeFromIp + downsizer + partnerDownsizer + giftingAmount + funeralBondPurchase;
        const residual = Math.max(0, netProceeds - totalAllocated);
        removeSoldInvestmentProperty(data.assets, ipAsset.id!);
        if (!data.assets.bankAccounts) data.assets.bankAccounts = [];
        if (residual > 0) {
            if (routesIpProceedsToEntityPool(factFind, ipAsset)) {
                const entityId = getEntityIdFromOwnership(ipAsset.ownership);
                const structure = (factFind.structures || []).find((s) => s.id === entityId);
                const name = structure?.name?.trim() || 'Trust or company';
                creditLiquidProceeds(data.assets, residual, {
                    ownership: entityOwnershipValue(entityId!),
                    id: `rv-ip-residual-${entityId}`,
                    description: `Residual from investment property sale — ${name} (after village entry)`,
                    mergeEntityId: entityId!,
                });
            } else {
                const existingResidual = data.assets.bankAccounts.find((a: any) => a.id === 'rv-ip-residual');
                if (existingResidual) {
                    existingResidual.value = residual;
                    existingResidual.ownership = ipAsset.ownership || 'joint';
                } else {
                    data.assets.bankAccounts.push({
                        id: 'rv-ip-residual',
                        type: 'bankAccounts',
                        description: 'Residual from investment property sale (after village entry)',
                        value: residual,
                        /* interest, not capital growth — see bankPlugin.ts */ incomeRate: 2, growthRate: 0,
                        ownership: ipAsset.ownership || 'joint',
                    });
                }
            }
        } else {
            const existingResidual = data.assets.bankAccounts.find((a: any) => a.id === 'rv-ip-residual');
            if (existingResidual) existingResidual.value = 0;
        }
        applyDownsizerContributionsToAssets(
            data.assets,
            factFind,
            {
                downsizerContribution: downsizer,
                partnerDownsizerContribution: partnerDownsizer,
                commenceIncomeStream: ipDetails.commenceIncomeStream === true,
            },
            {
                idPrefix: 'rv-ip-downsizer',
                assetId: 'rv-ip-downsizer-client',
                partnerAssetId: 'rv-ip-downsizer-partner',
            },
        );
        if (funeralBondPurchase > 0) {
            if (!data.assets.funeralBonds) data.assets.funeralBonds = [];
            data.assets.funeralBonds.push({ id: 'rv-ip-funeral-bond', type: 'funeralBonds', description: 'Funeral bond (from IP sale)', value: funeralBondPurchase, ownership: 'client' });
        }
        // What the sale did not cover comes from savings — see topUpEntryFeeFromLiquid.
        const topUp = topUpEntryFeeFromLiquid(data, factFind, rvStrategy, entryFee - entryFeeFromIp);
        const entryFeePaid = entryFeeFromIp + topUp.deducted;
        return finalizeRvStrategyFactFind(
            { factFind: data, entryAlreadyPaidFromSale: entryFeePaid > 0, entryFeePaid, entryFeeCgtFromIp: cgt + topUp.entryFeeCgtFromIp },
            factFind,
            rvStrategy,
        );
    }

    if (fundFrom === 'liquid') {
        const entryFeeFunding = rvStrategy?.entryFeeFunding || {};
        const cgtOverrides = rvStrategy?.entryFeeCgtOverrides || {};
        const data = structuredClone(factFind);
        if (!data.assets) data.assets = {};
        const assets = data.assets;
        const { totalTowardEntry, entryFeeCgtFromIp } = applyRvLiquidEntryFeeFunding(data, factFind, entryFeeFunding, cgtOverrides, entryFee);
        void assets;
        const totalDeducted = totalTowardEntry + drawEntryFeeFromLiquidInOrder(data, entryFee - totalTowardEntry);
        // `entryFeePaid` is what the balance sheet could ACTUALLY raise, which is not always the
        // contracted price: the loop above takes what is there and stops. Reported so the engine can
        // strike the exit entitlement on money genuinely paid — a village entry cannot be part-paid,
        // and an underfunded one must never book a refund it did not buy (owner-caught, 2026-08-26).
        return finalizeRvStrategyFactFind({ factFind: data, entryAlreadyPaidFromSale: totalDeducted > 0, entryFeePaid: totalDeducted, entryFeeCgtFromIp }, factFind, rvStrategy);
    }

    if (fundFrom !== 'sell_home' || !rvStrategy.sellHomeDetails) {
        return finalizeRvStrategyFactFind(
            { factFind: structuredClone(factFind), entryAlreadyPaidFromSale: false, entryFeeCgtFromIp: 0 },
            factFind,
            rvStrategy,
        );
    }

    const details = rvStrategy.sellHomeDetails;
    const salePrice = details.salePrice || factFind.home?.value || 0;
    const sellingCosts = details.sellingCosts || salePrice * 0.025;
    const netProceeds = Math.max(0, salePrice - sellingCosts);

    const entryFeeAllocation = Math.min(entryFee, netProceeds);
    const downsizer = Math.min(DOWNSIZER_CONTRIBUTION_CAP, Math.max(0, details.downsizerContribution || 0));
    const partnerDownsizer = Math.min(DOWNSIZER_CONTRIBUTION_CAP, Math.max(0, details.partnerDownsizerContribution || 0));
    const giftingAmount = Math.max(0, details.giftingAmount || 0);
    const funeralBondPurchase = Math.max(0, details.funeralBondPurchase || 0);
    const purchaseAnnuity = Math.max(0, details.purchaseAnnuity || 0);
    const purchaseInvestmentProperty = Math.max(0, details.purchaseInvestmentProperty || 0);
    /*
     * STAMP DUTY ON THE PROPERTY BOUGHT (owner, 24 Sep 2026). The duty is money that leaves the
     * sale proceeds with the price and never comes back as an asset: it is paid to the state, and
     * for capital gains tax it is an incidental cost of acquisition, so it sits in the cost base
     * (ITAA 1997 s 110-35). Where no state was chosen the duty is nil and the screen says the duty
     * is not modelled — no state, no guess.
     */
    const investmentPropertyStampDuty = purchaseInvestmentProperty > 0 ? Math.max(0, parseFloat(String(details.investmentPropertyStampDuty ?? '')) || 0) : 0;
    const purchaseShares = Math.max(0, details.purchaseShares || 0);

    const totalAllocated = entryFeeAllocation + downsizer + partnerDownsizer + giftingAmount + funeralBondPurchase + purchaseAnnuity + purchaseInvestmentProperty + investmentPropertyStampDuty + purchaseShares;
    const residual = Math.max(0, netProceeds - totalAllocated);

    const data = structuredClone(factFind);
    data.home = data.home || {};
    data.home.value = 0;

    // Existing bank/ABP/shares etc. are left unchanged. Only add one residual-from-sale entry to bank (no double-count).
    if (!data.assets) data.assets = {};
    if (!data.assets.bankAccounts) data.assets.bankAccounts = [];
    const existingResidual = data.assets.bankAccounts.find((a: any) => a.id === 'rv-sell-residual');
    if (residual > 0 && !existingResidual) {
        data.assets.bankAccounts.push({
            id: 'rv-sell-residual',
            type: 'bankAccounts',
            description: 'Residual from home sale (after village entry)',
            value: residual,
            /* interest, not capital growth — see bankPlugin.ts */ incomeRate: 2, growthRate: 0,
            ownership: 'joint',
        });
    } else if (existingResidual && residual > 0) {
        existingResidual.value = residual;
    }

    applyDownsizerContributionsToAssets(
        data.assets,
        factFind,
        {
            downsizerContribution: downsizer,
            partnerDownsizerContribution: partnerDownsizer,
            commenceIncomeStream: details.commenceIncomeStream === true,
        },
        {
            idPrefix: 'rv-downsizer',
            assetId: 'rv-downsizer-client',
            partnerAssetId: 'rv-downsizer-partner',
        },
    );
    // The gift is allocated out of the sale proceeds above (it reduces `residual`), so the money has
    // already left. What was missing is the deprivation record — see recordRvGiftAsDeprivation.
    recordRvGiftAsDeprivation(data, giftingAmount, (rvStrategy as any)?.villageEntryDate || details.saleDate);

    if (funeralBondPurchase > 0) {
        if (!data.assets.funeralBonds) data.assets.funeralBonds = [];
        data.assets.funeralBonds.push({
            id: 'rv-funeral-bond',
            type: 'funeralBonds',
            description: 'Funeral bond (from home sale)',
            value: funeralBondPurchase,
            ownership: 'client',
        });
    }

    if (purchaseInvestmentProperty > 0) {
        if (!data.assets.investmentProperty) data.assets.investmentProperty = [];
        data.assets.investmentProperty.push({
            id: 'rv-sell-inv-prop',
            type: 'investmentProperty',
            description: 'Investment property (from home sale)',
            value: purchaseInvestmentProperty,
            ownership: 'client',
            rent: details.investmentPropertyRent || 0,
            expenses: details.investmentPropertyExpenses || 0,
            growthRate: ASSET_CLASS_ASSUMPTIONS.propertyGrowthPct,
            costBase: purchaseInvestmentProperty + investmentPropertyStampDuty,
            acquisitionDate: 'post',
        });
    }

    if (purchaseShares > 0) {
        if (!data.assets.shares) data.assets.shares = [];
        // Priced at the client's new-money profile (Conservative until they answer) — the same
        // portfolio, in personal names, as the strategy's super/pension legs assume in theirs.
        const split = personalSplitFor(newMoneyProfileOf(factFind));
        data.assets.shares.push({
            id: 'rv-sell-shares',
            type: 'shares',
            description: 'Shares (from home sale)',
            value: purchaseShares,
            ownership: 'client',
            incomeRate: split.incomeRatePct,
            growthRate: split.growthRatePct,
            costBase: purchaseShares,
            frankingCredit: split.frankingPct,
            reinvest: false,
        });
    }

    if (purchaseAnnuity > 0) {
        const clientAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) : 65;
        const partnerAge = factFind?.adviceFor === 'couple' && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null;

        // Allow the RV sell-home allocator to control how the annuity is structured, using
        // the same annuity engine as Aged Care / Support at Home.
        const rawType = details.rvAnnuityType || 'lifetime';
        const annuityType = rawType === 'fixedTerm' ? 'fixedTerm' : 'lifetime';

        const residualPercent = details.rvAnnuityResidualPercent || 0;
        const residualCapital = Math.min(Math.max(residualPercent / 100, 0), 1);

        const termYears = annuityType === 'fixedTerm' ? details.rvAnnuityTermYears || null : null;

        const indexation = !!details.rvAnnuityIndexation;
        const reversionary = !!details.rvAnnuityReversionary && !!partnerAge;

        const incomeOverride = details.rvAnnuityIncomeOverride != null ? details.rvAnnuityIncomeOverride : null;
        const owner = details.rvAnnuityOwner || 'client';
        const annuityRecord = buildAnnuityImpact(factFind, {
            annuityType,
            purchaseAmount: purchaseAnnuity,
            age: clientAge,
            partnerAge,
            owner,
            residualCapital,
            termYears,
            indexation,
            reversionary,
            incomeOverride: Number.isFinite(incomeOverride) ? incomeOverride : null,
        });
        if (annuityRecord && !annuityRecord.warnings) {
            if (!data.assets.annuities) data.assets.annuities = [];
            data.assets.annuities.push(annuityAssetFromRecord(annuityRecord, {
                id: annuityRecord.id || 'rv-sell-annuity',
                description: `${annuityType === 'lifetime' ? 'Lifetime' : 'Fixed-term'} annuity (from home sale)`,
                ownership: 'client',
            }));
        }
    }

    // What the sale did not cover comes from savings — see topUpEntryFeeFromLiquid.
    const topUp = topUpEntryFeeFromLiquid(data, factFind, rvStrategy, entryFee - entryFeeAllocation);
    const entryFeePaid = entryFeeAllocation + topUp.deducted;
    return finalizeRvStrategyFactFind(
        { factFind: data, entryAlreadyPaidFromSale: entryFeePaid > 0, entryFeePaid, entryFeeCgtFromIp: topUp.entryFeeCgtFromIp },
        factFind,
        rvStrategy,
    );
}

export { EXTRA_ALLOWABLE_AMOUNT, DOWNSIZER_CONTRIBUTION_CAP };
